// Corrected BLEManager.js
class BLEManager {
  constructor() {
    this.device = null;
    this.server = null;
    this.service = null;
    this.characteristic = null;
    this.isCollecting = false;
    this.startTime = 0;
    this.endTime = 0;
    this.dataCallback = null;
    this.eventListeners = {};
    this.packageCount = 0;

    // Store all raw data with timestamps
    this.allRawData = []; // All bytes in order
    this.allTimestamps = []; // Timestamp for each byte

    // Initialize data worker if available
    try {
      this.dataWorker = new Worker(new URL('./bleWorker.js', import.meta.url));
      this.setupWorkerListeners();
    } catch (error) {
      console.warn('Worker not available, using main thread processing');
      this.dataWorker = null;
    }
  }

  // Event emitter methods
  on(event, callback) {
    if (!this.eventListeners[event]) {
      this.eventListeners[event] = [];
    }
    this.eventListeners[event].push(callback);
  }

  off(event, callback) {
    if (this.eventListeners[event]) {
      this.eventListeners[event] = this.eventListeners[event].filter(cb => cb !== callback);
    }
  }

  emit(event, data) {
    if (this.eventListeners[event]) {
      this.eventListeners[event].forEach(callback => callback(data));
    }
  }

  setupWorkerListeners() {
    if (!this.dataWorker) return;

    this.dataWorker.onmessage = (event) => {
      if (event.data.type === 'progress' && this.dataCallback) {
        //console.log('Worker data length:', event.data.dataLength);
      }
    };

    this.dataWorker.onerror = (error) => {
      console.error('Worker error:', error);
    };
  }

  async connect() {
    if (this.device && this.device.gatt.connected) {
      console.log('Already connected to device:', this.device.name);
      return true;
    }

    try {

      this.device = await navigator.bluetooth.requestDevice({
        filters: [
          { namePrefix: 'Oxy' },
          { namePrefix: 'POD' }
        ],
        optionalServices: ['6e400001-b5a3-f393-e0a9-e50e24dcca9e']
      });


      this.device.addEventListener('gattserverdisconnected', this.handleDisconnect.bind(this));

      this.server = await this.device.gatt.connect();

      this.service = await this.server.getPrimaryService('6e400001-b5a3-f393-e0a9-e50e24dcca9e');

      this.characteristic = await this.service.getCharacteristic('6e400003-b5a3-f393-e0a9-e50e24dcca9e');

      return true;

    } catch (error) {
      console.error('Connection failed:', error);
      this.reset();
      throw error;
    }
  }

  handleDisconnect() {
    console.log('Device disconnected');
    this.emit('disconnect');
    this.reset();
  }

  reset() {
    this.device = null;
    this.server = null;
    this.service = null;
    this.characteristic = null;
    this.isCollecting = false;
    this.allRawData = [];
    this.allTimestamps = [];

    if (this.dataWorker) {
      this.dataWorker.postMessage({ type: 'reset' });
    }
  }

  async startDataCollection(callback) {
    if (!this.characteristic) {
      throw new Error('Device not connected');
    }

    if (this.isCollecting) {
      console.log('Data collection already in progress');
      return this.startTime;
    }

    console.log('Starting data collection...');
    this.isCollecting = true;
    this.startTime = Date.now();
    this.dataCallback = callback;
    this.allRawData = [];
    this.allTimestamps = [];
    this.packageCount = 0;

    try {
      await this.characteristic.startNotifications();
      this.characteristic.addEventListener('characteristicvaluechanged', this.handleValueChange.bind(this));

      console.log('Notifications started, collecting data...');
      return this.startTime;

    } catch (error) {
      console.error('Failed to start notifications:', error);
      this.isCollecting = false;
      throw error;
    }
  }

  handleValueChange(event) {
    if (!this.isCollecting) return;

    this.packageCount++;  // Increment package counter

    const value = event.target.value;
    const rawData = new Uint8Array(value.buffer);
    const timestamp = Date.now();

    const dataArray = Array.from(rawData);




    this.allRawData.push(...dataArray);
    this.allTimestamps.push(timestamp);






    // Send to callback for UI visualization
    if (this.dataCallback) {
      this.dataCallback(dataArray);
    }

    // Send to worker if available
    if (this.dataWorker) {
      this.dataWorker.postMessage({
        type: 'processData',
        data: dataArray
      });
    }

  }

  // Return all data with timestamps
  async getCurrentData() {
    if (!this.isCollecting) {
      return null;
    }

    return {
      startTime: this.startTime,
      endTime: Date.now(),
      rawData: [...this.allRawData], // All bytes
      timestamps: [...this.allTimestamps], // Timestamp for each byte
      totalDataPoints: this.allRawData.length
    };
  }

  async stopDataCollection() {
    if (!this.characteristic || !this.isCollecting) {
      console.log('Data collection not active');
      return null;
    }

    console.log('Stopping data collection...');

    try {
      this.isCollecting = false;
      await this.characteristic.stopNotifications();
      this.characteristic.removeEventListener('characteristicvaluechanged', this.handleValueChange);
      this.endTime = Date.now();

      console.log('=== FINAL COUNTS ===');
      console.log('Total packages received:', this.packageCount);
      console.log('Total timestamps stored:', this.allTimestamps.length);
      console.log('Difference:', this.packageCount - this.allTimestamps.length);

      const collectionData = {
        startTime: this.startTime,
        endTime: this.endTime,
        rawData: [...this.allRawData],
        timestamps: [...this.allTimestamps],
        totalDataPoints: this.allRawData.length
      };


      return collectionData;

    } catch (error) {
      console.error('Failed to stop notifications:', error);
      throw error;
    }
  }

  async disconnect() {
    console.log('Disconnecting from device...');

    if (this.device?.gatt?.connected) {
      try {
        if (this.isCollecting) {
          await this.stopDataCollection();
        }
        await this.device.gatt.disconnect();
        console.log('Device disconnected successfully');
      } catch (error) {
        console.error('Error disconnecting:', error);
      }
    }
    this.reset();
  }

  // Utility methods
  getConnectionStatus() {
    return {
      isConnected: this.device?.gatt?.connected || false,
      isCollecting: this.isCollecting,
      deviceName: this.device?.name || null,
      dataPoints: this.allRawData.length
    };
  }

  getStartTime() {
    return this.startTime;
  }

  getEndTime() {
    return this.endTime;
  }

  // Clean up resources
  destroy() {
    this.disconnect();
    if (this.dataWorker) {
      this.dataWorker.terminate();
      this.dataWorker = null;
    }
    this.eventListeners = {};
  }
}

export default new BLEManager();